import React, { useEffect, useState, useCallback } from "react";
import { ColumnDef } from "@tanstack/react-table";
import apiClient, { fetchPermissions, fetchApps, fetchAppUsers } from "../api/client";
import { PermissionSnapshot, PermissionUser, Workspace, AppSnapshot, AppUser } from "../types";
import DataTable from "../components/DataTable";
import ExportButton from "../components/ExportButton";
import DetailModal from "../components/DetailModal";
import WorkspaceCombobox, { isPersonalWorkspace } from "../components/WorkspaceCombobox";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime } from "../utils/datetime";

function roleColor(role: string): string {
  const r = role.toLowerCase();
  if (r === "admin") return "text-red-700 font-semibold";
  if (r === "member" || r === "contributor" || r === "readwrite") return "text-orange-700 font-semibold";
  if (r === "viewer" || r === "read") return "text-green-700";
  return "text-gray-600";
}

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center py-16 text-gray-400">
      <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
      {label}
    </div>
  );
}

type ActiveTab = "report" | "dataset" | "apps";

// ---------------------------------------------------------------------------
// Permissions (report / dataset) tab
// ---------------------------------------------------------------------------

function PermissionsTab({ artifactType, workspaceFilter, workspaces, excludedWorkspaceIds }: {
  artifactType: "report" | "dataset";
  workspaceFilter: string;
  workspaces: Workspace[];
  excludedWorkspaceIds: Set<string>;
}) {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [snapshots, setSnapshots] = useState<PermissionSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalSnapshot, setModalSnapshot] = useState<PermissionSnapshot | null>(null);
  const [modalUsers, setModalUsers] = useState<PermissionUser[] | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string> = { artifact_type: artifactType };
    if (workspaceFilter) params.workspace_id = workspaceFilter;

    apiClient
      .get<PermissionSnapshot[]>("/api/permissions", { params })
      .then((res) => setSnapshots(res.data))
      .catch((err) => setError(err.message ?? "Failed to load permissions."))
      .finally(() => setLoading(false));
  }, [artifactType, workspaceFilter]);

  useEffect(() => {
    fetchData();
    setModalSnapshot(null);
    setModalUsers(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactType, workspaceFilter]);

  const handleOpenModal = async (snapshot: PermissionSnapshot) => {
    setModalSnapshot(snapshot);
    setModalUsers(null);
    setModalLoading(true);
    try {
      const detail = await fetchPermissions(snapshot.artifactId);
      setModalUsers(detail.users);
    } catch {
      setModalUsers([]);
    } finally {
      setModalLoading(false);
    }
  };

  const adminCount = (users: PermissionUser[]) =>
    users.filter((u) => (u.role ?? "").toLowerCase() === "admin").length;

  const visibleSnapshots = snapshots.filter(
    (s) => !s.workspaceId || !excludedWorkspaceIds.has(s.workspaceId)
  );

  const columns: ColumnDef<PermissionSnapshot, string>[] = [
    { accessorKey: "artifactName", header: `${artifactType === "report" ? "Report" : "Dataset"} Name`, cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "workspaceName", header: "Workspace", cell: ({ getValue }) => getValue() || "—" },
    {
      accessorKey: "snapshotAt",
      header: "Last Snapshot",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      id: "userCount",
      header: "User Count",
      accessorFn: (row) => String(row.users?.length ?? 0),
      cell: ({ getValue }) => getValue(),
    },
    {
      id: "adminCount",
      header: "Admin Count",
      accessorFn: (row) => String(adminCount(row.users ?? [])),
      cell: ({ getValue }) => (
        <span className={parseInt(getValue()) > 0 ? "text-red-700 font-semibold" : "text-gray-600"}>
          {getValue()}
        </span>
      ),
    },
    {
      id: "expand",
      header: "",
      cell: ({ row }) => (
        <button
          onClick={(e) => { e.stopPropagation(); handleOpenModal(row.original); }}
          className="text-xs text-blue-600 hover:underline whitespace-nowrap"
        >
          Users
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {loading && <Spinner label="Loading permissions…" />}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchData} className="ml-4 text-red-600 underline text-xs">Retry</button>
        </div>
      )}

      {!loading && !error && (
        <DataTable data={visibleSnapshots} columns={columns} initialPageSize={25} />
      )}

      {modalSnapshot && (
        <DetailModal
          title={`Users — ${modalSnapshot.artifactName}`}
          subtitle={modalSnapshot.workspaceName}
          onClose={() => { setModalSnapshot(null); setModalUsers(null); }}
        >
          {modalLoading ? (
            <div className="flex items-center gap-2 text-gray-400 text-sm py-8 justify-center">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Loading users…
            </div>
          ) : modalUsers && modalUsers.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <th className="pb-2 pr-4">Display Name</th>
                  <th className="pb-2 pr-4">Email</th>
                  <th className="pb-2 pr-4">Role</th>
                  <th className="pb-2">Principal Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {modalUsers.map((u, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="py-1.5 pr-4 text-gray-700">{u.displayName || "—"}</td>
                    <td className="py-1.5 pr-4 text-gray-500">{u.email || "—"}</td>
                    {/* An unrecorded role reads as unknown, not as no role
                        at all — the distinction matters on an access review. */}
                    <td className={`py-1.5 pr-4 ${roleColor(u.role ?? "")}`}>{u.role || "—"}</td>
                    <td className="py-1.5 text-gray-500">{u.principalType || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-400 text-sm">No users found for this artifact.</p>
          )}
        </DetailModal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Apps tab
// ---------------------------------------------------------------------------

function AppsTab({ workspaceFilter, excludedWorkspaceIds }: {
  workspaceFilter: string;
  excludedWorkspaceIds: Set<string>;
}) {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [apps, setApps] = useState<AppSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalApp, setModalApp] = useState<AppSnapshot | null>(null);
  const [modalUsers, setModalUsers] = useState<AppUser[] | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string> = {};
    if (workspaceFilter) params.workspace_id = workspaceFilter;

    fetchApps(params)
      .then((data) => setApps(data))
      .catch((err) => setError(err.message ?? "Failed to load apps."))
      .finally(() => setLoading(false));
  }, [workspaceFilter]);

  useEffect(() => {
    fetchData();
    setModalApp(null);
    setModalUsers(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceFilter]);

  const handleOpenModal = async (app: AppSnapshot) => {
    setModalApp(app);
    setModalUsers(null);
    setModalLoading(true);
    try {
      const users = await fetchAppUsers(app.appId);
      setModalUsers(users);
    } catch {
      setModalUsers([]);
    } finally {
      setModalLoading(false);
    }
  };

  const visibleApps = apps.filter(
    (a) => !a.workspaceId || !excludedWorkspaceIds.has(a.workspaceId)
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<AppSnapshot, any>[] = [
    { accessorKey: "appName", header: "App Name", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "workspaceName", header: "Workspace", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "publishedBy", header: "Published By", cell: ({ getValue }) => getValue() || "—" },
    {
      accessorKey: "lastUpdate",
      header: "Last Updated",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      id: "userCount",
      header: "User Count",
      accessorFn: (row) => row.users?.length ?? 0,
      cell: ({ getValue }) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
          {getValue()}
        </span>
      ),
    },
    {
      id: "expand",
      header: "",
      cell: ({ row }) => (
        <button
          onClick={(e) => { e.stopPropagation(); handleOpenModal(row.original); }}
          className="text-xs text-blue-600 hover:underline whitespace-nowrap"
        >
          Users
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ExportButton
          endpoint="/api/export/apps"
          params={{ workspace_id: workspaceFilter || undefined }}
          filename="apps-export.csv"
        />
      </div>

      {loading && <Spinner label="Loading apps…" />}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchData} className="ml-4 text-red-600 underline text-xs">Retry</button>
        </div>
      )}

      {!loading && !error && visibleApps.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
          <p className="text-lg font-semibold">No apps found</p>
        </div>
      )}

      {!loading && !error && visibleApps.length > 0 && (
        <DataTable data={visibleApps} columns={columns} initialPageSize={25} />
      )}

      {modalApp && (
        <DetailModal
          title={`Users — ${modalApp.appName}`}
          subtitle={modalApp.workspaceName}
          onClose={() => { setModalApp(null); setModalUsers(null); }}
        >
          {modalLoading ? (
            <div className="flex items-center gap-2 text-gray-400 text-sm py-8 justify-center">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Loading users…
            </div>
          ) : modalUsers && modalUsers.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <th className="pb-2 pr-4">Display Name</th>
                  <th className="pb-2 pr-4">Email</th>
                  <th className="pb-2 pr-4">Role</th>
                  <th className="pb-2">Principal Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {modalUsers.map((u, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="py-1.5 pr-4 text-gray-700">{u.displayName || "—"}</td>
                    <td className="py-1.5 pr-4 text-gray-500">{u.email || "—"}</td>
                    {/* An unrecorded role reads as unknown, not as no role
                        at all — the distinction matters on an access review. */}
                    <td className={`py-1.5 pr-4 ${roleColor(u.role ?? "")}`}>{u.role || "—"}</td>
                    <td className="py-1.5 text-gray-500">{u.principalType || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-400 text-sm">No users found for this app.</p>
          )}
        </DetailModal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AccessControlPage() {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [wsLoading, setWsLoading] = useState(true);
  const [wsError, setWsError] = useState<string | null>(null);
  const [wsReloadKey, setWsReloadKey] = useState(0);
  const [activeTab, setActiveTab] = useState<ActiveTab>("report");
  const [workspaceFilter, setWorkspaceFilter] = useState("");
  // Personal ("My workspace") workspaces are noise for SoD reviews — hidden by default.
  const [hidePersonal, setHidePersonal] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setWsLoading(true);
    setWsError(null);
    // SOX scope: monitored workspaces only. include_personal=true because this
    // page filters personal workspaces client-side so its toggle can reveal them.
    apiClient
      .get<Workspace[]>("/api/workspaces", { params: { include_personal: "true", monitored_only: "true" } })
      .then((res) => { if (!cancelled) setWorkspaces(res.data); })
      .catch((err) => { if (!cancelled) setWsError(err?.message ?? "Failed to load workspaces."); })
      .finally(() => { if (!cancelled) setWsLoading(false); });
    return () => { cancelled = true; };
  }, [wsReloadKey]);

  const personalWorkspaceIds = React.useMemo(
    () => new Set(workspaces.filter(isPersonalWorkspace).map((ws) => ws.workspaceId)),
    [workspaces]
  );
  const excludedWorkspaceIds = hidePersonal ? personalWorkspaceIds : new Set<string>();
  const selectableWorkspaces = hidePersonal
    ? workspaces.filter((ws) => !personalWorkspaceIds.has(ws.workspaceId))
    : workspaces;

  const toggleHidePersonal = () => {
    const next = !hidePersonal;
    // If the active filter points at a personal workspace that is about to be
    // hidden, clear it so the page doesn't keep a filter it no longer displays.
    if (next && workspaceFilter && personalWorkspaceIds.has(workspaceFilter)) {
      setWorkspaceFilter("");
    }
    setHidePersonal(next);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Access Control</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Report, dataset, and app permissions in monitored workspaces — SoD evidence.
          </p>
        </div>
        {(activeTab === "report" || activeTab === "dataset") && (
          <ExportButton
            endpoint="/api/export/permissions"
            params={{
              artifact_type: activeTab,
              workspace_id: workspaceFilter || undefined,
            }}
            filename="permissions-export.csv"
          />
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {(["report", "dataset", "apps"] as ActiveTab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); }}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors capitalize ${
              activeTab === tab
                ? "border-blue-700 text-blue-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab === "report" ? "Reports" : tab === "dataset" ? "Datasets" : "Apps"}
          </button>
        ))}
      </div>

      {/* Workspace filter + personal-workspace toggle */}
      <div className="flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-600">Workspace</label>
          <WorkspaceCombobox
            workspaces={selectableWorkspaces}
            value={workspaceFilter}
            onChange={setWorkspaceFilter}
            loading={wsLoading}
          />
          {wsError && (
            <span className="text-xs text-red-600">
              {wsError}{" "}
              <button
                onClick={() => setWsReloadKey((k) => k + 1)}
                className="underline hover:text-red-700"
              >
                Retry
              </button>
            </span>
          )}
        </div>

        <label className="flex items-center gap-2 cursor-pointer select-none" title="Personal ('My workspace') workspaces are hidden by default">
          <button
            type="button"
            role="switch"
            aria-checked={hidePersonal}
            onClick={toggleHidePersonal}
            className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
              hidePersonal ? "bg-blue-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transform transition-transform ${
                hidePersonal ? "translate-x-[18px]" : "translate-x-0.5"
              }`}
            />
          </button>
          <span className="text-sm text-gray-600">
            Hide personal workspaces
            {hidePersonal && personalWorkspaceIds.size > 0 && (
              <span className="ml-1.5 text-xs text-gray-400">
                ({personalWorkspaceIds.size} hidden)
              </span>
            )}
          </span>
        </label>
      </div>

      {(activeTab === "report" || activeTab === "dataset") && (
        <PermissionsTab
          artifactType={activeTab}
          workspaceFilter={workspaceFilter}
          workspaces={workspaces}
          excludedWorkspaceIds={excludedWorkspaceIds}
        />
      )}
      {activeTab === "apps" && (
        <AppsTab workspaceFilter={workspaceFilter} excludedWorkspaceIds={excludedWorkspaceIds} />
      )}
    </div>
  );
}
